import React from 'react'

import {
  AlignItems,
  Box,
  Color,
  Display,
  DisplayPosition,
  FlexDirection,
  FontWeight,
  JustifyContent,
  Text,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

import type { WelcomeDiscountBannerData } from '../types'
import { DiscountPerWeek } from './DiscountPerWeek'

const ProgressBarContainer = withCitrusTheme(Box, () => ({
  width: '100%',
  height: '16px',
  position: 'relative',
  marginBottom: '16px',
}))

const ProgressTrack = withCitrusTheme(Box, (theme) => ({
  backgroundColor: theme.color(Color.ColdGrey_500),
  height: '8px',
  borderRadius: '4px',
}))

const ProgressFill = withCitrusTheme(Box, (theme) => ({
  backgroundColor: theme.color(Color.Primary_600),
  width: '50%',
  height: '100%',
  borderRadius: '4px',
  animation: 'progress 2s linear',
  '@keyframes progress': {
    '0%': { width: '0%' },
    '100%': { width: '50%' },
  },
}))

const ActiveCircle = withCitrusTheme(Box, (theme) => ({
  backgroundColor: theme.color(Color.Primary_600),
  width: '16px',
  height: '16px',
  borderRadius: '50%',
}))

const InactiveCircle = withCitrusTheme(Box, (theme) => ({
  backgroundColor: theme.color(Color.ColdGrey_500),
  border: `2px solid ${theme.color(Color.ColdGrey_300)}`,
  width: '16px',
  height: '16px',
  borderRadius: '50%',
}))

const RoundedBox = withCitrusTheme(Box, () => ({
  borderRadius: 8,
}))

type DiscountBannerProps = {
  discountBanner: WelcomeDiscountBannerData | null
}

function hasUsableDiscountBanner(
  discountBanner: WelcomeDiscountBannerData | null,
): discountBanner is WelcomeDiscountBannerData {
  return Boolean(discountBanner && discountBanner.discountDetails.length === 3)
}

export function DiscountBanner({ discountBanner }: DiscountBannerProps) {
  if (!hasUsableDiscountBanner(discountBanner)) {
    return null
  }

  return (
    <RoundedBox
      data-testid="discount-banner"
      display={Display.Flex}
      alignItems={AlignItems.Center}
      justifyContent={JustifyContent.Center}
      borderRadius={1.5}
      bg={Color.ColdGrey_800}
      color={Color.White}
      aria-label="Apply welcome offer discount"
      paddingV={4}
      paddingH={3}
      tabIndex={0}
    >
      <Box
        display={Display.Flex}
        alignItems={AlignItems.Center}
        flexDirection={FlexDirection.Column}
        gap={2}
        width={['90%', '70%', '70%', '70%']}
      >
        <Text fontWeight={FontWeight.Bold} size={2} color={Color.White}>
          {discountBanner.title}
        </Text>
        <ProgressBarContainer>
          <Box
            position={DisplayPosition.Absolute}
            left="0"
            right="0"
            top="50%"
            style={{ transform: 'translateY(-50%)', zIndex: 0 }}
          >
            <ProgressTrack>
              <ProgressFill />
            </ProgressTrack>
          </Box>
          <Box
            display={Display.Flex}
            justifyContent={JustifyContent.SpaceBetween}
            width="100%"
            position={DisplayPosition.Relative}
            style={{ zIndex: 1 }}
          >
            <Box width="90px" display={Display.Flex} justifyContent={JustifyContent.Center}>
              <ActiveCircle />
            </Box>
            <Box width="90px" display={Display.Flex} justifyContent={JustifyContent.Center}>
              <ActiveCircle />
            </Box>
            <Box width="90px" display={Display.Flex} justifyContent={JustifyContent.Center}>
              <InactiveCircle />
            </Box>
          </Box>
        </ProgressBarContainer>
        <DiscountPerWeek discountDetails={discountBanner.discountDetails} />
      </Box>
    </RoundedBox>
  )
}
